import { useLayoutEffect, useState } from "react";

/** The content card's stable anchor id, emitted by the library's AppShell. */
const CONTENT_ANCHOR_ID = "__dialog-anchor";

export interface AnchorInsets {
  top: number;
  right: number;
  bottom: number;
}

const ZERO_INSETS: AnchorInsets = { bottom: 0, right: 0, top: 0 };

/**
 * Viewport-relative insets of the content card, for pinning a right-side
 * sheet INSIDE the content area (below the site header, inside the shell
 * gutter) instead of over the full viewport. A sheet viewport is
 * `fixed inset-0`, so the pin is applied as margins on the popup. This is
 * THE side-panel presentation — every right-side sheet uses it so the UX
 * never forks.
 *
 * Falls back to zero insets (full-height sheet) when the content anchor is
 * absent — e.g. a route outside the shell.
 */
export function useContentAnchorInsets(open: boolean): AnchorInsets {
  const [insets, setInsets] = useState<AnchorInsets>(ZERO_INSETS);

  // Layout effect: the margins must be on the popup before first paint, both
  // to avoid a visible reposition on open and so the fullscreen↔sidebar FLIP
  // measures the popup's true final rect.
  useLayoutEffect(() => {
    if (!open) return;
    const el = document.getElementById(CONTENT_ANCHOR_ID);
    if (!el) {
      setInsets(ZERO_INSETS);
      return;
    }
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setInsets({
        bottom: Math.max(0, window.innerHeight - rect.bottom),
        right: Math.max(0, window.innerWidth - rect.right),
        top: Math.max(0, rect.top),
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  return insets;
}

/**
 * The popup classes that pair with the insets: the sheet rounds and borders
 * like a panel of the page (matching the content card's `rounded="md"`)
 * rather than a full-height layer.
 */
export const ANCHORED_SHEET_POPUP_CLASS = "rounded-md border";
